import * as Yup from 'yup';
import t from 'react-translate';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  GoalItemStatus,
  MentoringProgramGoalItem,
} from 'redux/schemas/models/mentoring-program-goal-items';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { css } from '@emotion/react';
import {
  halfSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { gray7 } from 'styles/global_defaults/colors';
import NvIcon from 'shared/components/nv-icon';
import { useAppDispatch } from 'redux/store';
import {
  createMentorshipProgramGoalItem,
  deleteMentorshipProgramGoalItem,
  updateMentorshipProgramGoalItem,
} from 'redux/actions/mentoring-program-goal-items';
import MentoringProgramContext from '../mentoring-program/context';
import { GOAL_ITEM_DESCRIPTION_MAX_LENGTH } from '../mentoring-program/constants';
import GoalDescription from './goal-description';
import GoalMenu from './goal-menu';

type GoalItemFormData = Partial<Omit<MentoringProgramGoalItem, 'id'>>;

type GoalItemProps = {
  item: MentoringProgramGoalItem;
  isReadOnly: boolean;
  isEditing: boolean;
  setEditingId: (id: number) => void;
};

const styles = (isReadOnly: boolean) => css`
  display: grid;
  gap: ${halfSpacing}px;
  grid-template-columns: ${standardSpacing}px 1fr auto;
  padding: ${halfSpacing}px;

  &.highlight-item {
    &:hover {
      background: ${gray7};
      border-radius: ${quarterSpacing / 2}px;
      .delete-goal-item {
        ${!isReadOnly
        && css`
          opacity: 1;
        `}
      }
    }
  }
`;

const GoalItem = ({
  item,
  isReadOnly,
  isEditing,
  setEditingId,
}: GoalItemProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: mentorshipProgramId, mentorshipProgramEnrollment: enrollmentId } = mentoringProgram || {};
  const [formData, setFormData] = useState<GoalItemFormData>();

  const validationSchema = Yup.object().shape({
    description: Yup.string().max(
      GOAL_ITEM_DESCRIPTION_MAX_LENGTH,
      t.VALIDATION.MAX_LENGTH(`${GOAL_ITEM_DESCRIPTION_MAX_LENGTH}`),
    ),
    status: Yup.string().oneOf(Object.values(GoalItemStatus)),
  });

  const defaultValues = useMemo(
    () => ({
      status: item.status,
      description: item.description,
    }),
    [item],
  );

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });
  const {
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const { description, status } = watch();

  const onChange = useCallback(() => {
    if (formData && description && !errors.description) {
      if (item.id) {
        dispatch(
          updateMentorshipProgramGoalItem({
            mentorshipProgramId,
            id: item.id,
            enrollmentId,
            description: formData.description,
            status: formData.status,
          }),
        );
      } else {
        dispatch(
          createMentorshipProgramGoalItem({
            mentorshipProgramId,
            enrollmentId,
            status: formData.status,
            description: formData.description,
          }),
        );
        reset();
      }
    }
    setEditingId(null);
  }, [formData]);

  const onReset = () => {
    reset(defaultValues);
  };

  useEffect(() => {
    if (item.id) {
      setValue('description', item.description);
    }
  }, [item]);

  useEffect(() => {
    const { unsubscribe } = watch(setFormData);
    return () => unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (status !== item.status) {
      onChange();
    }
  }, [status]);

  return (
    <FormProvider {...methods}>
      <form>
        <div
          css={styles(isReadOnly)}
          className={`${
            item.id && !isEditing ? 'highlight-item' : ''
          } align-items-center`}
        >
          <NvIcon
            icon={item.id ? 'goal' : 'create-new-post'}
            size='smallest'
            className='gray-4 text-align-center'
          />
          <GoalDescription
            isReadOnly={isReadOnly}
            goalItemId={item.id}
            onChange={onChange}
            onReset={onReset}
            isEditing={isEditing}
            description={description}
            setEditingId={setEditingId}
          />
          <GoalMenu
            isReadOnly={isReadOnly}
            item={item}
            disabled={!description || !!errors.description}
            isEditing={isEditing}
            status={status}
            onChange={onChange}
            onDelete={() => dispatch(
              deleteMentorshipProgramGoalItem({
                mentorshipProgramId,
                id: item.id,
                enrollmentId,
              }),
            )}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default GoalItem;
